// Answer Assist - Anonymous event telemetry.
//
// Per docs/Technical Specification.md section 6:
//   - Events only. NEVER worksheet content, NEVER photos, NEVER text the user typed or dictated.
//   - Anonymous device identifier only. No emails, no names, no Personally Identifiable Information (PII).
//   - Stored in the dedicated Supabase project `mydyslexiatools`, table `events`.
//   - The user can opt out from Settings; respected here at the source.
//
// In v0 this module logs to the browser console only. The Supabase wiring is added in v0.2
// once the Supabase project exists. The public surface (`telemetry.track`) stays the same so
// nothing breaks when we plug in the real backend.

type EventName =
  | 'app_opened'
  | 'worksheet_capture_started'
  | 'worksheet_capture_accepted'
  | 'worksheet_capture_rejected'
  | 'worksheet_review_shown'
  | 'worksheet_processed'
  | 'answer_method'
  | 'answer_cleaned'
  | 'worksheet_answers_complete'
  | 'worksheet_exported'
  | 'session_ended';

interface EventPayload {
  [key: string]: string | number | boolean | undefined;
}

const STORAGE_KEY_OPT_OUT = 'mdt_telemetry_opt_out';
const STORAGE_KEY_DEVICE_ID = 'mdt_device_id';

function isOptedOut(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_OPT_OUT) === 'true';
  } catch {
    return false;
  }
}

function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEY_DEVICE_ID);
    if (existing) return existing;
    const fresh = generateUuid();
    localStorage.setItem(STORAGE_KEY_DEVICE_ID, fresh);
    return fresh;
  } catch {
    return 'anonymous';
  }
}

function generateUuid(): string {
  // Standard v4 UUID generator. Random data only, no time component, no MAC address.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    // Fallback for environments without the Web Crypto API. Not cryptographically random,
    // which is fine here because the identifier is only an anonymous device tag.
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function emit(event: EventName, payload: EventPayload) {
  const enriched = {
    event,
    ts: new Date().toISOString(),
    device_id: getDeviceId(),
    app: 'answer-assist',
    version: '0.1.0',
    ...payload,
  };

  // v0: console only. v0.2: POST to a Supabase edge function that inserts into the events table.
  // eslint-disable-next-line no-console
  console.info('[telemetry]', enriched);
}

export const telemetry = {
  track(event: EventName, payload: EventPayload = {}) {
    if (isOptedOut()) return;
    try {
      emit(event, payload);
    } catch (e) {
      // Telemetry must never break the user experience.
      // eslint-disable-next-line no-console
      console.warn('[telemetry] failed', e);
    }
  },

  setOptOut(optOut: boolean) {
    try {
      localStorage.setItem(STORAGE_KEY_OPT_OUT, String(optOut));
    } catch {
      // Storage unavailable - silently ignore.
    }
  },

  isOptedOut,
};
